import { API_NAME, STREAMING_API_URL } from '#config/runtimeconfig';
import {
    formatChatResponse,
    formatConversationDetail,
    formatConversationsResponse,
} from './legacyFormat';

import API from '/b2b/common/helpers/API';
import { ASSISTANT_MODES } from './constants';
import { amplifyOptions } from '/b2b/amplify.config';
import { handleError } from '/b2b/common/services/helpers';

export const fetchConversations = (mode, { page, pageSize } = {}) => {
    const params = {};
    if (mode) params.mode = mode;
    if (page && page > 1) params.page = page;
    if (pageSize) params.page_size = pageSize; // eslint-disable-line camelcase
    const options = Object.keys(params).length ? { queryStringParameters: params } : undefined;
    return API.get(API_NAME, `/assistant/chat`, options)
        .catch(handleError)
        .then(formatConversationsResponse);
};

export const fetchConversationById = (conversationId, { cursor, pageSize, mode } = {}) => {
    const params = {};
    if (cursor) params.cursor = cursor;
    if (pageSize) params.page_size = pageSize; // eslint-disable-line camelcase
    if (mode) params.mode = mode;
    const options = Object.keys(params).length ? { queryStringParameters: params } : undefined;
    return API.get(API_NAME, `/assistant/chat/${conversationId}`, options)
        .catch(handleError)
        .then(formatConversationDetail);
};

export const sendChatMessage = (message, conversationId, mode = ASSISTANT_MODES.AI) => {
    const body = { message, mode };
    if (conversationId) {
        body.conversation_id = conversationId; // eslint-disable-line camelcase
    }
    return API.post(API_NAME, `/assistant/chat`, { body })
        .catch(handleError)
        .then(formatChatResponse);
};

/**
 * Sends a chat message with SSE streaming enabled.
 * Uses raw fetch instead of Amplify API since Amplify reads the entire response body.
 *
 * The stream emits OpenAI-compatible chunks:
 *   - choices[0].delta.content        → text tokens
 *   - choices[0].delta.reasoning_content → thinking steps
 *   - top-level citations             → source references
 *   - top-level conversation_id       → backend conversation ID (first frame, new convos)
 *   - choices[0].finish_reason='stop' → stream complete
 *
 * @param {string} message
 * @param {string|null} conversationId
 * @param {object} options
 * @param {AbortSignal} [options.signal]
 * @param {function} options.onToken - called with each text token
 * @param {function} [options.onReasoning] - called with each reasoning step string
 * @param {function} [options.onCitations] - called with the citations array when it arrives
 * @param {function} [options.onConversationId] - called with conversation_id from first frame
 * @param {function} [options.onMessageId] - called with the server-assigned message id from first frame
 * @param {function} [options.onDone] - called when the stream completes
 * @param {function} [options.onError] - called on stream error
 * @returns {Promise<void>}
 */
export const sendChatMessageStream = async (message, conversationId, options = {}) => {
    const {
        signal,
        onToken,
        onReasoning,
        onCitations,
        onConversationId,
        onMessageId,
        onDone,
        onError,
    } = options;

    const headers = await amplifyOptions.API.REST.headers({ apiName: API_NAME });

    const response = await fetch(`${STREAMING_API_URL}/assistant/chat`, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            message,
            ...(conversationId ? { conversation_id: conversationId } : {}), // eslint-disable-line camelcase
            stream: true,
        }),
        signal,
    });

    if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        let errorCode;
        try {
            const errorBody = await response.json();
            errorCode = errorBody?.error_code || errorBody?.resp_msg?.error_code;
            errorMessage = errorBody?.message || errorBody?.resp_msg?.message || errorMessage;
        } catch {
            // Couldn't parse error body
        }
        if (onError) onError({ message: errorMessage, errorCode });
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6).trim();
                    if (!data) continue;

                    if (data === '[DONE]') {
                        if (onDone) onDone();
                        return;
                    }

                    try {
                        const parsed = JSON.parse(data);

                        if (parsed.status === 'error' || parsed.error) {
                            if (onError)
                                onError({
                                    message: parsed.message || 'Stream error',
                                    errorCode: parsed.error_code,
                                });
                            return;
                        }

                        // Surface conversation_id from the first frame for new conversations
                        if (parsed.conversation_id && onConversationId) {
                            onConversationId(parsed.conversation_id);
                        }

                        // Surface the server-assigned message id so the client placeholder can be updated
                        if (parsed.id && onMessageId) {
                            onMessageId(parsed.id);
                        }

                        const delta = parsed.choices?.[0]?.delta;

                        // Annotations are url_citation entries the model explicitly used inline
                        const annotations = delta?.annotations?.filter(
                            a => a.type === 'url_citation'
                        );
                        if (annotations?.length && onCitations) {
                            onCitations(annotations);
                        }

                        if (delta?.content && onToken) {
                            onToken(delta.content);
                        }

                        if (delta?.reasoning_content && onReasoning) {
                            onReasoning(delta.reasoning_content);
                        }

                        const finishReason = parsed.choices?.[0]?.finish_reason;
                        if (finishReason === 'stop' || parsed.done || parsed.finished) {
                            if (onDone) onDone();
                            return;
                        }
                    } catch {
                        // Non-JSON line — treat as a raw text token
                        if (onToken) onToken(data);
                    }
                }
            }
        }

        if (onDone) onDone();
    } catch (error) {
        if (error.name !== 'AbortError') {
            if (onError) onError(error);
            throw error;
        }
    }
};

/**
 * Escalates a conversation to the privacy team by creating a HelpScout conversation.
 */
export const escalateConversation = (conversationId, payload) => {
    const options = {
        body: {
            message: payload.text,
            subject: payload.subject,
            mode: ASSISTANT_MODES.HUMAN,
            ai_conversation_id: conversationId, // eslint-disable-line camelcase
        },
    };
    return API.post(API_NAME, `/assistant/chat`, options)
        .catch(handleError)
        .then(formatChatResponse);
};

export const deleteConversation = conversationId =>
    API.del(API_NAME, `/assistant/chat/${conversationId}`).catch(handleError);

export const submitFeedback = (conversationId, messageId, sentiment) => {
    const options = {
        body: {
            conversation_id: conversationId, // eslint-disable-line camelcase
            message_id: messageId, // eslint-disable-line camelcase
            feedback: {
                sentiment,
            },
        },
    };
    return API.post(API_NAME, `/assistant/feedback`, options).catch(handleError);
};
